<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/label';
import {FunctionAPI, HeaderInfo} from '@react-spectrum/docs';
import packageData from '@react-aria/label/package.json';

```tsx import
import {useLabel} from '@react-aria/label';
```

---
category: Utilities
keywords: [label, form, aria]
---

# useLabel

<p>{docs.exports.useLabel.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useLabel']} />

## API

<FunctionAPI function={docs.exports.useLabel} links={docs.links} />

## Example

The `useLabel` hook associates a label with a field. It automatically handles
creating an id for the field and associates the label with it.

```tsx example
function ColorField(props) {
  let {labelProps, fieldProps} = useLabel(props);

  return (
    <>
      <label {...labelProps}>{props.label}</label>
      <select {...fieldProps}>
        <option>Indigo</option>
        <option>Maroon</option>
        <option>Chartreuse</option>
      </select>
    </>
  );
}

<ColorField label="Favorite color" />
```

By default, `useLabel` assumes that the label is a native HTML label element.
However, if you are labeling a non-native form element, be sure to use an
element other than a `<label>` and set the `labelElementType` prop appropriately.

See [useRadioGroup](useRadioGroup.html#example) and [useTextField](useTextField.html#example)
for examples of how `useLabel` is used by components.
